/**
 * Axios API client for PIChecklistPortalService (Coral awsJson dispatch).
 *
 * Two modes determined at build time by SERVER_MODE:
 *
 * 1. LOCAL DEV (SERVER_MODE=standalone, `npm run server`):
 *    - Proxies to `http://localhost:8080` (the local `bb server`)
 *    - No auth — CloudAuth is disabled on the local backend
 *    - Passes the alias via `x-pi-acting-alias` header (the backend's
 *      HeaderCallerResolver reads it when cloudauth.disable=true)
 *    - The alias defaults to 'gsinaksh'; override via localStorage key
 *      `pi-dev-alias` for testing as different users
 *
 * 2. PRODUCTION (Mons deploy):
 *    - Calls the per-stage backend URL (injected via /api CFN replacement)
 *    - Sends the browser's Midway session credentials (withCredentials); the
 *      backend validates the Midway identity token server-side.
 *    - TODO(midway-auth): the server-side Midway validator (CoralSSOHandler) ships
 *      in a follow-up CR — see PIChecklistPortalService docs/PROJECT_CHECKLIST.md
 *      §5.1. The previous SigV4 signing path was removed: it targeted a CloudAuth
 *      backend that expects Bearer tokens, not SigV4, and its MIDWAY_ROLE_ARN
 *      define was never injected — it could never have worked.
 */
import axios from 'axios';
import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

declare const API_BASE_URL: string;
declare const SERVER_MODE: string;

/** Axios config extension: lets the bootstrap call opt out of the 401/403 redirect. */
export interface AuthAwareRequestConfig extends InternalAxiosRequestConfig {
  skipAuthRedirect?: boolean;
}

/** True in local standalone dev (webpack dev-server + alias-header proxy). */
export const isLocalDev =
  typeof SERVER_MODE !== 'undefined' && SERVER_MODE === 'standalone';
const baseURL = isLocalDev
  ? ''
  : typeof API_BASE_URL !== 'undefined'
  ? API_BASE_URL
  : '';

const apiClient = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
  // Send the Midway session cookies cross-origin so the backend can validate them.
  withCredentials: !isLocalDev,
});

function getLocalDevAlias(): string {
  try {
    return localStorage.getItem('pi-dev-alias') ?? 'gsinaksh';
  } catch {
    return 'gsinaksh';
  }
}

async function localDevInterceptor(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  config.headers.set('x-pi-acting-alias', getLocalDevAlias());
  return config;
}

if (isLocalDev) {
  apiClient.interceptors.request.use(localDevInterceptor);
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // The GetCurrentUser bootstrap sets skipAuthRedirect so AuthBootstrap can
    // render its inline Access Denied / retry screens; every other call gets
    // the blanket redirect on an expired/invalid session.
    const skipRedirect = (error.config as AuthAwareRequestConfig | undefined)
      ?.skipAuthRedirect;
    if (!isLocalDev && !skipRedirect) {
      const status = error.response?.status;
      if (status === 401) {
        // Session missing/expired mid-use → re-authenticate via the broker's /login
        // (SSO round-trip), not the generic auth-error page.
        window.location.assign('/login');
      } else if (status === 403) {
        // Authenticated but forbidden (e.g. not a PI member) → do NOT loop through
        // login; surface the auth-error page.
        if (window.location.pathname !== '/auth-error') {
          window.location.href = '/auth-error';
        }
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
